// strategyGrouping.js — "같은 prefix = 한 전략" 패밀리 그룹핑.
//
// paper-trader 는 "같은 가설(H번호), 다른 코인/파라미터" 를 별도 strategy_id 로
// 운영한다:
//   코인 변형 : h318_bnb / h318_sol / h318_doge   → 본질은 H318
//   파라미터  : h1106_tight / h1106_lenient / ...  → 본질은 H1106
//   base+변형 : h1112 / h1112_lenient              → 본질은 H1112
//
// 운영 대시보드에서 이들을 하나의 "패밀리" 카드로 묶어 합산 stats 로 본다.
//
// 그룹 기준(단일 소스): 백엔드 /api/summary 가 주는 `family`(= 선두 h<숫자>) 를
// 우선 사용하고, 없으면 동일 규칙(^h\d+)으로 프론트에서 도출한다. 백엔드
// internal/web/family.go(FamilyBase/VariantAxis)와 규칙이 일치한다.
//
//   ※ 이전 버전은 코인 접미사만 묶고 _tight/_lenient 같은 파라미터 변형은
//     묶지 않았다. 같은 H번호의 파라미터 변형도 한 전략으로 보도록 일반화했다.

import { readStrategyFields } from "../components/paper/PaperStrategyCard";

/**
 * 코인 토큰 화이트리스트(소문자). config.toml [symbols].all 운영 universe 기준.
 * variant_axis 분류(coin vs param)에만 쓰인다. 신규 코인은 여기에만 추가.
 */
export const KNOWN_COINS = new Set([
    "btc",
    "eth",
    "sol",
    "xrp",
    "ada",
    "bnb",
    "doge",
    "aave",
    "link",
]);

const FAMILY_RE = /^h\d+/;

/** id 에서 선두 h<숫자> family 토큰을 도출(백엔드 FamilyBase 와 동일). */
function deriveBase(id) {
    const m = FAMILY_RE.exec(String(id ?? ""));
    return m ? m[0] : String(id ?? "");
}

/** id 의 family 뒤 접미사를 coin / param / base 로 분류(백엔드 VariantAxis 동일). */
function deriveAxis(id) {
    const s = String(id ?? "");
    const base = deriveBase(s);
    if (s === base) return "base";
    const suffix = s.slice(base.length).replace(/^_/, "");
    if (!suffix) return "base";
    const last = suffix.split("_").pop().toLowerCase();
    return KNOWN_COINS.has(last) ? "coin" : "param";
}

/** 전략(객체/문자열)의 family id — 백엔드 `family` 우선, 없으면 도출. */
export function familyOf(s) {
    if (s && typeof s === "object") {
        const fam = s.family ?? s.Family;
        if (fam) return fam;
        return deriveBase(s.Strategy ?? s.strategy ?? "");
    }
    return deriveBase(s);
}

/** 전략(객체/문자열)의 variant_axis — 백엔드 `variant_axis` 우선, 없으면 도출. */
export function variantAxisOf(s) {
    if (s && typeof s === "object") {
        const ax = s.variant_axis ?? s.VariantAxis;
        if (ax) return ax;
        return deriveAxis(s.Strategy ?? s.strategy ?? "");
    }
    return deriveAxis(s);
}

/**
 * 멤버 axis 배열 → family 수준 축(coin / param / mixed / base).
 * 백엔드 familyAxis 와 동일 규칙.
 */
export function familyAxis(axes) {
    if (!Array.isArray(axes) || axes.length === 0) return "base";
    if (axes.length === 1) return axes[0];
    let allCoin = true;
    let anyCoin = false;
    for (const a of axes) {
        if (a === "coin") anyCoin = true;
        else allCoin = false;
    }
    if (allCoin) return "coin";
    if (!anyCoin) return "param";
    return "mixed";
}

/**
 * strategy_id 를 { base, coin } 으로 분해(하위호환). base 는 family(^h\d+),
 * coin 은 코인 접미사(없으면 null — 파라미터 변형 포함).
 *
 * @param {string} id 예) "h318_bnb" → {base:"h318", coin:"bnb"},
 *                       "h1106_tight" → {base:"h1106", coin:null}
 */
export function parseStrategyId(id) {
    if (typeof id !== "string" || id.length === 0) {
        return { base: id ?? "", coin: null };
    }
    const base = deriveBase(id);
    if (deriveAxis(id) === "coin") {
        const suffix = id.slice(base.length).replace(/^_/, "");
        return { base, coin: suffix.split("_").pop().toLowerCase() };
    }
    return { base, coin: null };
}

/**
 * 멤버 strategy 객체 배열의 TradeStats 를 합산해 그룹 집계값을 만든다.
 * paper-trader /api/summary 의 lowercase JSON 키(pnl_krw, trades, ...)를 사용.
 */
export function aggregateStats(members) {
    const agg = {
        trades: 0,
        wins: 0,
        losses: 0,
        pnl_krw: 0,
        gross_win: 0,
        gross_loss: 0,
        open_count: 0,
    };
    let lastTradeTs = null;
    let firstTradeTs = null;
    for (const m of members) {
        agg.trades += num(m.trades ?? m.Trades);
        agg.wins += num(m.wins ?? m.Wins);
        agg.losses += num(m.losses ?? m.Losses);
        agg.pnl_krw += num(m.pnl_krw ?? m.PnLKRW);
        agg.gross_win += num(m.gross_win ?? m.GrossWin);
        agg.gross_loss += num(m.gross_loss ?? m.GrossLoss);
        agg.open_count += num(m.open_count ?? m.OpenCount);

        const lt = m.last_trade_ts ?? m.LastTradeTs;
        if (lt) {
            const t = new Date(lt).getTime();
            if (Number.isFinite(t) && (lastTradeTs === null || t > lastTradeTs)) {
                lastTradeTs = t;
            }
        }
        const ft = m.first_trade_ts ?? m.FirstTradeTs;
        if (ft) {
            const t = new Date(ft).getTime();
            if (Number.isFinite(t) && (firstTradeTs === null || t < firstTradeTs)) {
                firstTradeTs = t;
            }
        }
    }
    agg.win_rate = agg.trades > 0 ? agg.wins / agg.trades : 0;
    // PF: gross_loss 가 0 이면 N/A(-1) — 서버 ProfitFct 규약과 동일.
    agg.profit_factor = agg.gross_loss > 0 ? agg.gross_win / agg.gross_loss : -1;
    agg.last_trade_ts = lastTradeTs !== null ? new Date(lastTradeTs).toISOString() : undefined;
    agg.first_trade_ts =
        firstTradeTs !== null ? new Date(firstTradeTs).toISOString() : undefined;
    return agg;
}

/**
 * strategies 배열을 패밀리(^h\d+) 단위로 그룹핑한다.
 *
 * - 같은 family(코인이든 파라미터 변형이든)에 멤버가 2개 이상이면 그룹 카드로 묶는다.
 * - 멤버가 1개뿐인 family 는 묶지 않고 원본 단일 카드로 둔다.
 * - 입력 등장 순서를 보존한다(그룹은 첫 멤버가 등장한 위치에 자리잡음).
 *
 * 그룹 객체 형태(readStrategyFields 호환 + 그룹 메타):
 *   { isGroup, strategy:<baseId>, baseId, variantAxis, category,
 *     members:[...pnl desc], memberCount, ...aggregateStats(members) }
 */
export function groupStrategies(strategies) {
    if (!Array.isArray(strategies)) return [];

    const order = [];
    const buckets = new Map(); // family -> { base, members:[] }
    strategies.forEach((s) => {
        const base = familyOf(s);
        if (!buckets.has(base)) {
            buckets.set(base, { base, members: [] });
            order.push(base);
        }
        buckets.get(base).members.push(s);
    });

    const out = [];
    for (const base of order) {
        const b = buckets.get(base);
        if (b.members.length >= 2) {
            out.push(makeGroup(b.base, b.members));
        } else {
            out.push(b.members[0]);
        }
    }
    return out;
}

function makeGroup(baseId, members) {
    const sorted = [...members].sort(
        (a, b) => readStrategyFields(b).pnl - readStrategyFields(a).pnl,
    );
    const category = readStrategyFields(sorted[0]).category ?? "Other";
    const variantAxis = familyAxis(sorted.map((m) => variantAxisOf(m)));
    return {
        isGroup: true,
        strategy: baseId,
        baseId,
        variantAxis,
        category,
        members: sorted,
        memberCount: sorted.length,
        ...aggregateStats(sorted),
    };
}

function num(v) {
    const n = typeof v === "number" ? v : Number(v);
    return Number.isFinite(n) ? n : 0;
}
